import { Button, Form } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { anyPass, isEmpty, isNil, omit, reject } from 'ramda';
import { FC, useCallback, useEffect, useState } from 'react';

import { DateFilter, EmployeesFilter, FormItem, ProjectsFilter, Select } from '@/atoms';
import { DEFAULT_FILTER_KEYS } from '@/constants';
import { useFilterPage, useWindowDimensions } from '@/hooks';
import { useGetTestsListQuery } from '@/services';
import { FiltersItem } from '@/types';
import { deletedOptions, isEmptyObject, statusOptions } from '@/utils';

import styles from './styles.module.scss';

interface Props {
  mobileDimension: number;
  onDeletedClear: () => void;
  onMobileSubmit: () => void;
}

const clearObject = reject(anyPass([isEmpty, isNil]));

const convertProjectsOrEmployees = (entities: string | string[]) => {
  if (entities) {
    if (Array.isArray(entities)) {
      return entities.map((id: string) => ({ value: +id }));
    }

    return [{ value: +entities }];
  }

  return [];
};

const convertDates = (dates: string | string[]) => {
  if (dates) {
    if (Array.isArray(dates)) {
      return dates.map((date: string) => dayjs(date));
    }
  }

  return [];
};

export const TestsPageFilters: FC<Props> = ({
  mobileDimension,
  onDeletedClear,
  onMobileSubmit,
}) => {
  const { filter, resetFilter, setFilter } = useFilterPage();

  const windowDimensions = useWindowDimensions();
  const windowWidth = windowDimensions.width;
  const [isMobile, setIsMobile] = useState(windowWidth < mobileDimension);

  useEffect(() => {
    setIsMobile(windowWidth < mobileDimension);
  }, [windowWidth]);

  const [form] = useForm<{
    createdAt: Dayjs[];
    createdBy: FiltersItem[];
    deleted?: string;
    projects: FiltersItem[];
    publishedAt: Dayjs[];
    status: string;
  }>();

  const RESET_FILTER_KEYS = ['offset', 'page', ...DEFAULT_FILTER_KEYS];
  const data = useWatch([], form);

  const onFinish = useCallback(() => {
    onDeletedClear();
    resetFilter({
      ...omit(RESET_FILTER_KEYS, filter),
      ...omit(['projects'], clearObject(data)),
      ...(data.projects.length > 0 ? { projects: data.projects.map((item) => item.value) } : {}),
      ...(data.createdBy.length > 0 ? { createdBy: data.createdBy.map((item) => item.value) } : {}),
      ...(data?.createdAt?.length > 0
        ? { createdAt: data.createdAt.map((item) => item.toISOString()) }
        : {}),
      ...(data?.publishedAt?.length > 0
        ? { publishedAt: data.publishedAt.map((item) => item.toISOString()) }
        : {}),
      ...(data?.deleted && { deleted: data.deleted === 'true' }),
    });
    if (isMobile) {
      onMobileSubmit();
    }
  }, [data, setFilter, isMobile]);

  const handleReset = () => {
    onDeletedClear();
    resetFilter(omit(RESET_FILTER_KEYS, filter));
    if (isMobile) {
      onMobileSubmit();
    }
  };

  const { isFetching } = useGetTestsListQuery(
    {
      ...omit(['page'], filter),
    },
    {
      skip: true,
    },
  );

  const initialValues = {
    createdAt: convertDates(filter?.createdAt),
    createdBy: convertProjectsOrEmployees(filter?.createdBy),
    deleted: filter?.deleted,
    projects: convertProjectsOrEmployees(filter?.projects),
    publishedAt: convertDates(filter?.publishedAt),
    status: filter?.status,
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      className={styles.filtersForm}
      initialValues={initialValues}
    >
      <div className={styles.container}>
        <FormItem name="projects" label="Проект" flex>
          <ProjectsFilter />
        </FormItem>
        <FormItem name="createdBy" label="Сотрудник" flex>
          <EmployeesFilter />
        </FormItem>
        <FormItem name="status" label="Статус" flex>
          <Select options={statusOptions} />
        </FormItem>
        <FormItem name="createdAt" label="Период создания" flex>
          <DateFilter />
        </FormItem>
        <FormItem name="publishedAt" label="Период публикации" flex>
          <DateFilter />
        </FormItem>
        <FormItem name="deleted" label="Удаленные интерактивы" flex>
          <Select options={deletedOptions} />
        </FormItem>
      </div>
      <div className={styles.filtersBtnWrapper}>
        <Button
          className={classNames(styles.filtersBtn, styles.resetFilters)}
          disabled={isEmptyObject(data)}
          onClick={handleReset}
          htmlType="reset"
        >
          Сбросить
        </Button>

        <Button
          htmlType="submit"
          disabled={isFetching}
          className={classNames(styles.filtersBtn, styles.filtersSubmitBtn)}
          type="primary"
        >
          Применить
        </Button>
      </div>
    </Form>
  );
};
